import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import { Film, Plus } from 'lucide-react'
import { GenerateButton, PromptInput, VideoPreview, FeatureGate, MobileBottomBar } from '@/components/ui'
import { VideoPickerModal } from '@/components/features/video'
import { AnalyticsService } from '@/services/analytics'
import type { Asset } from '@/types'

export function ExtendVideoPage() {
  const { user } = useUser()

  useEffect(() => {
    AnalyticsService.trackViewItem('video_tools', 'extend_video', 'Extend Video', user?.id)
    AnalyticsService.trackViewContent('Extend Video', user?.id)
  }, [user?.id])

  return (
    <FeatureGate requiredPlan="creator" featureName="Extend Video" upgradePlans={['creator', 'pro']}>
      <ExtendVideoContent />
    </FeatureGate>
  )
}

function ExtendVideoContent() {
  const location = useLocation()
  const navigate = useNavigate()
  const [selectedVideo, setSelectedVideo] = useState<Asset | null>(null)
  const [prompt, setPrompt] = useState('')
  const [showPicker, setShowPicker] = useState(false)

  // Load video from route state if passed
  useEffect(() => {
    const state = location.state as { video?: Asset } | null
    if (state?.video) {
      setSelectedVideo(state.video)
    }
  }, [location.state])

  const isValid = selectedVideo && prompt.trim().length > 0

  const handleExtendVideo = () => {
    if (!selectedVideo || !prompt.trim()) return

    // Navigate to generation page with extend video params
    // The API call will be made there while showing the booting screen
    navigate('/generation/new', {
      state: {
        type: 'video',
        prompt: prompt.trim(),
        extendVideoParams: {
          generation_id: selectedVideo.id,
          prompt: prompt.trim(),
          prompt_enhancer: true,
        },
      },
    })
  }

  return (
    <>
      <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6 pb-24 md:pb-8">
        {/* Header */}
        <div>
          <h1 className="text-xl font-semibold text-white">Extend Video</h1>
          <p className="text-sm text-[#666] mt-1">
            Select a video and describe how you want to extend it
          </p>
        </div>

        {/* Video Selection */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-[#888]">Source Video</label>
          
          {selectedVideo ? (
            <VideoPreview
              src={selectedVideo.url}
              title={selectedVideo.prompt?.slice(0, 50) || `Video ${selectedVideo.id.slice(0, 8)}`}
              subtitle={selectedVideo.prompt}
              onRemove={() => setSelectedVideo(null)}
            />
          ) : (
            <button
              onClick={() => setShowPicker(true)}
              className="w-full aspect-video rounded-xl border-2 border-dashed border-white/[0.08] hover:border-white/[0.15] bg-white/[0.02] hover:bg-white/[0.03] transition-colors flex flex-col items-center justify-center gap-3"
            >
              <div className="p-4 rounded-full bg-white/[0.05]">
                <Film className="size-8 text-[#555]" />
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-[#888]">Select a video to extend</p>
                <p className="text-xs text-[#555] mt-1">Choose from your generated videos</p>
              </div>
              <span className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.05] text-sm text-white/80">
                <Plus className="size-4" />
                Browse Videos
              </span>
            </button>
          )}
        </div>

        {/* Model Info (hidden - auto-selected based on source video) */}
        {/* {selectedVideo?.model_id && (
          <div className="flex items-center gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/[0.06]">
            <div className="flex-1">
              <p className="text-xs text-[#666]">Model</p>
              <p className="text-sm text-white mt-0.5">
                Using <span className="font-medium">{selectedVideo.model_id}</span> model
              </p>
            </div>
            <span className="px-3 py-1.5 rounded-lg text-xs font-medium border bg-violet-500/20 text-violet-300 border-violet-500/30">
              Auto-selected
            </span>
          </div>
        )} */}

        {/* Prompt Input */}
        <PromptInput
          value={prompt}
          onChange={setPrompt}
          placeholder="Describe how you want to extend the video... The character walks towards the camera and waves..."
          label="Extension Prompt"
          rows={3}
        />

        {/* Generate Button - Desktop only */}
        <div className="hidden md:flex justify-end">
          <GenerateButton
            disabled={!isValid}
            onClick={handleExtendVideo}
            className="w-full md:w-auto md:min-w-[200px]"
          >
            <Film className="size-5" />
            Extend Video
          </GenerateButton>
        </div>
      </div>

      {/* Fixed bottom bar - Mobile only (with iOS safe area) */}
      <MobileBottomBar>
        <GenerateButton
          disabled={!isValid}
          onClick={handleExtendVideo}
          className="w-full"
        >
          <Film className="size-5" />
          Extend Video
        </GenerateButton>
      </MobileBottomBar>

      {/* Video Picker Modal */}
      <VideoPickerModal
        isOpen={showPicker}
        onClose={() => setShowPicker(false)}
        onSelect={setSelectedVideo}
      />
    </>
  )
}
